import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './styles/tokens.css';
import './styles/global.css';
import App from './App';
import { hydrateFromCache, refreshLiveRates } from './lib/format/finmindFx';
import { initFirebaseAppCheck, getAppCheckToken, appCheckReady } from './lib/firebase';
import { configureServerAuth } from './lib/auth/serverRefresh';

// Hydrate FX rates from localStorage cache synchronously so the first
// render uses live values (not stale fixed fallback). Then kick off a
// background refresh — non-blocking; failures are silent and the static
// fallback in fx.ts always keeps conversion working.
hydrateFromCache();
void refreshLiveRates();

// Warn if VITE_GOOGLE_CLIENT_ID is missing — LoginScreen needs it to build
// the OAuth redirect URL.
if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) {
  // eslint-disable-next-line no-console
  console.error(
    '[Mochia] VITE_GOOGLE_CLIENT_ID is not set. Create .env.local with the OAuth client id and restart the dev server.',
  );
}

// Wire serverRefresh dependencies BEFORE starting App Check init, so the
// authedFetch helper has the right token provider and readiness gate even
// if the very first React useEffect fires before init resolves. The
// readiness gate (appCheckReady) makes authedFetch wait until init has
// settled, which avoids the cold-load race where the App Check header is
// dropped and the request hits 401 app_check_failed (the 2026-05-23
// ycb10133030 incident).
configureServerAuth({ getAppCheckToken, appCheckReady });

// Kick off App Check init. Non-blocking — appCheckReady resolves when this
// finishes (success, env-var miss, or thrown error — see firebase.ts).
void initFirebaseAppCheck();

const container = document.getElementById('app-root');
if (!container) {
  throw new Error('Mount node #app-root missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
